import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import ConfirmDialog from '@/components/ConfirmDialog';
import { BookOpen, Sparkles, Loader2, CheckCircle2, Circle, Clock, XCircle, ArrowLeft, Crown, RefreshCw, Star, Wand2, Palette, FileText, Image, Volume2, Settings, LayoutGrid, Play, Film } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get full image URL
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Step definitions for the progress indicator (UI display steps)
// Order matches the actual V2 pipeline execution order
const STEPS = [
  { key: 'structure', label: 'Creating characters', Icon: Star },
  { key: 'narrative', label: 'Writing story', Icon: FileText },
  { key: 'splitting', label: 'Organizing pages', Icon: BookOpen },
  { key: 'layout', label: 'Designing layouts', Icon: LayoutGrid },
  { key: 'prompts', label: 'Designing illustrations', Icon: Palette },
  { key: 'images', label: 'Painting illustrations', Icon: Wand2 },
  { key: 'videos', label: 'Animating scenes', Icon: Film },
  { key: 'audio', label: 'Recording narration', Icon: Volume2 },
  { key: 'thumbnail', label: 'Creating cover', Icon: Image },  // Runs at 95%, near end
  { key: 'finalizing', label: 'Finishing touches', Icon: Settings },
];

// Map backend step keys to UI step keys (V2 pipeline has additional steps)
const STEP_KEY_MAP = {
  // V1 pipeline steps (direct mapping)
  'structure': 'structure',
  'thumbnail': 'thumbnail',
  'narrative': 'narrative',
  'splitting': 'splitting',
  'layout': 'layout',
  'prompts': 'prompts',
  'images': 'images',
  'videos': 'videos',
  'audio': 'audio',
  'finalizing': 'finalizing',
  'saving': 'finalizing',
  // V2 pipeline steps (map to closest UI step)
  'illustration_bible': 'structure',  // Character design → Creating characters
  'portraits': 'structure',           // Character portraits → Creating characters
  'directives': 'prompts',            // Visual planning → Designing illustrations
  'visual_subtext': 'prompts',        // Visual details → Designing illustrations
  'continuity': 'prompts',            // Consistency check → Designing illustrations
  'building': 'prompts',              // Building pages → Designing illustrations
  'validating': 'prompts',            // Validation → Designing illustrations
  'creating_story': 'prompts',        // Creating story → Designing illustrations
  'video_selection': 'videos',        // Video selection → Animating scenes
};

const GeneratingPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const eventSourceRef = useRef(null);

  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isPremium, setIsPremium] = useState(false);

  // Track max progress to prevent UI from going backwards
  const maxProgressRef = useRef(0);

  // Fetch user premium status
  useEffect(() => {
    const checkPremium = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setIsPremium(res.data.is_premium || false);
      } catch {
        // Ignore - user might not be authenticated
      }
    };
    checkPremium();
  }, []);

  // Handle retry for failed jobs
  const handleRetry = async () => {
    setRetrying(true);
    try {
      const response = await axios.post(`${API}/v1/jobs/${jobId}/retry`, {}, {
        withCredentials: true
      });
      toast.success('Retrying your story!');
      navigate(`/generating/${response.data.job_id}`);
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to retry. Please try again.';
      toast.error(errorMsg);
      setRetrying(false);
    }
  };

  // Handle cancel job
  const handleCancelJob = async () => {
    setCancelling(true);
    try {
      await axios.delete(`${API}/v1/jobs/${jobId}`, { withCredentials: true });
      toast.success('Story cancelled. No credits were charged.');
      setShowCancelDialog(false);
      navigate('/create');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
      setCancelling(false);
    }
  };

  // Fetch initial job status
  useEffect(() => {
    const fetchJob = async () => {
      try {
        const response = await axios.get(`${API}/v1/jobs/${jobId}`, {
          withCredentials: true
        });
        setJob(response.data);
        setLoading(false);

        // If already completed, redirect to story (replace history to prevent back-button loop)
        if (response.data.status === 'completed' && response.data.story_id) {
          navigate(`/story/${response.data.story_id}`, { replace: true });
        }
      } catch (err) {
        console.error('Failed to fetch job:', err);
        setError(err.response?.data?.detail || 'Failed to load job status');
        setLoading(false);
      }
    };

    fetchJob();
  }, [jobId, navigate]);

  // Set up SSE connection for real-time updates
  useEffect(() => {
    if (!jobId || loading) return;

    // SSE uses cookie-based auth (withCredentials sends cookies)
    // We rely on the polling fallback for environments where SSE auth fails
    const streamUrl = `${API}/v1/jobs/${jobId}/stream`;

    // Create EventSource for SSE (cookies sent via withCredentials)
    const eventSource = new EventSource(streamUrl, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setConnected(true);
    };

    eventSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'complete') {
          toast.success('Your story is ready!');
          eventSource.close();
          navigate(`/story/${data.story_id}`, { replace: true });
        } else if (data.type === 'failed') {
          setError(data.error || 'Story generation failed');
          eventSource.close();
        } else if (data.type === 'status') {
          // Track max progress to prevent UI from going backwards
          if (data.progress && data.progress > maxProgressRef.current) {
            maxProgressRef.current = data.progress;
          }
          setJob(prevJob => ({
            ...prevJob,
            ...data,
            // Use max progress to prevent backwards movement
            progress: Math.max(data.progress || 0, maxProgressRef.current),
          }));

          // Auto-redirect when complete (100%)
          if (data.progress >= 100 && data.story_id) {
            eventSource.close();
            toast.success('Your story is ready!');
            navigate(`/story/${data.story_id}`, { replace: true });
          }
        }
      } catch (err) {
        console.error('Failed to parse SSE message:', err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('SSE error:', err);
      setConnected(false);
      eventSource.close();
    };

    // Fallback polling in case SSE fails or misses completion
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/v1/jobs/${jobId}`, {
          withCredentials: true
        });
        const data = response.data;

        if (data.status === 'completed' && data.story_id) {
          clearInterval(pollInterval);
          eventSource.close();
          toast.success('Your story is ready!');
          navigate(`/story/${data.story_id}`, { replace: true });
        } else if (data.status === 'failed') {
          clearInterval(pollInterval);
          eventSource.close();
          setError(data.error || 'Story generation failed');
        } else {
          // Track max progress to prevent UI from going backwards
          if (data.progress && data.progress > maxProgressRef.current) {
            maxProgressRef.current = data.progress;
          }
          // Update job state with latest data (use max progress)
          setJob({
            ...data,
            progress: Math.max(data.progress || 0, maxProgressRef.current),
          });

          // Auto-redirect when complete (100%)
          if (data.progress >= 100 && data.story_id) {
            clearInterval(pollInterval);
            eventSource.close();
            toast.success('Your story is ready!');
            navigate(`/story/${data.story_id}`, { replace: true });
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000); // Poll every 3 seconds as backup

    return () => {
      eventSource.close();
      clearInterval(pollInterval);
    };
  }, [jobId, loading, navigate]);

  // Get current step index (maps V2 pipeline steps to UI steps)
  const getCurrentStepIndex = () => {
    if (!job) return -1;
    // Map the backend step to the UI step key
    const uiStepKey = STEP_KEY_MAP[job.step] || job.step;
    return STEPS.findIndex(s => s.key === uiStepKey);
  };

  // Render step status icon
  const renderStepIcon = (step, index) => {
    const currentIndex = getCurrentStepIndex();

    if (job?.status === 'failed') {
      return <XCircle className="w-5 h-5 text-red-400" />;
    }

    if (index < currentIndex) {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }

    if (index === currentIndex) {
      return <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />;
    }

    return <Circle className="w-5 h-5 text-gray-300" />;
  };

  // Render step detail text
  const getStepDetail = (step, index) => {
    const currentIndex = getCurrentStepIndex();

    if (index < currentIndex) {
      return <span className="text-green-600">Done</span>;
    }

    if (index === currentIndex) {
      if (step.key === 'images' && job) {
        return <span className="text-orange-500">{job.pages_with_images}/{job.total_pages}</span>;
      }
      if (step.key === 'audio' && job) {
        return <span className="text-orange-500">{job.pages_with_audio}/{job.total_pages}</span>;
      }
      return <span className="text-orange-500">In progress</span>;
    }

    return <span className="text-gray-400">-</span>;
  };

  if (loading) {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="w-16 h-16 mx-auto mb-4 text-orange-400 animate-spin" />
            <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
              Loading...
            </p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (error && job?.status !== 'queued') {
    return (
      <PageLayout>
        <div className="flex-1 flex items-center justify-center py-8">
          <div className="max-w-md w-full mx-4">
            <div className="rounded-3xl p-8 shadow-lg text-center" style={{ background: 'var(--st-bg-surface)', border: '2px solid var(--st-border-light)' }}>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/30">
                <XCircle className="w-10 h-10 text-red-400" />
              </div>

              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                Story Generation Failed
              </h2>

              <p className="text-gray-600 mb-6" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                {error}
              </p>

              {/* Show which step failed if available */}
              {job?.step && job.step !== 'failed' && (
                <div className="mb-6 p-3 rounded-xl bg-orange-50/50 dark:bg-white/5" style={{ border: '1px solid var(--st-border-light)' }}>
                  <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                    Failed during: <span className="font-medium">{STEPS.find(s => s.key === job.step)?.label || job.step}</span>
                  </p>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <button
                  onClick={handleRetry}
                  disabled={retrying}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white disabled:opacity-50"
                  style={{
                    background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                    fontFamily: 'Chewy, cursive'
                  }}
                >
                  {retrying ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-5 h-5" />
                      Retry Story
                    </>
                  )}
                </button>

                <button
                  onClick={() => navigate('/create')}
                  className="w-full px-6 py-3 rounded-xl font-bold border-2"
                  style={{
                    borderColor: 'var(--st-border-light)',
                    color: 'var(--st-text-heading)',
                    fontFamily: 'Quicksand, sans-serif'
                  }}
                >
                  Create New Story
                </button>

                <button
                  onClick={() => navigate('/library')}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  style={{ fontFamily: 'Quicksand, sans-serif' }}
                >
                  Go to Library
                </button>
              </div>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Queued state
  if (job?.status === 'queued') {
    const queueVersion = job?.story_version || 1;
    return (
      <PageLayout>
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-md mx-auto px-4 py-10">
            <div className="rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>

              {/* Header with centered icon */}
              <div className="pt-8 pb-4 px-6 text-center">
                {/* Centered icon */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center shadow-lg"
                      style={{
                        background: 'linear-gradient(135deg, #FFB347 0%, var(--st-primary-hover) 100%)',
                        boxShadow: '0 8px 24px rgba(255, 179, 71, 0.35)'
                      }}
                    >
                      <Clock className="w-9 h-9 text-white" />
                    </div>
                    <div
                      className="absolute -top-1 -right-1 px-2 py-0.5 rounded-full text-xs font-bold"
                      style={{
                        background: queueVersion === 3 ? '#8B5CF6' : queueVersion === 2 ? '#3B82F6' : '#6B7280',
                        color: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      v{queueVersion}
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  In Queue
                </h1>

                {/* Queue position */}
                <div className="my-4">
                  <span
                    className="inline-block text-5xl font-bold px-6 py-3 rounded-2xl"
                    style={{
                      fontFamily: 'Chewy, cursive',
                      color: 'var(--st-primary)',
                      background: 'linear-gradient(135deg, rgba(255, 140, 97, 0.1) 0%, rgba(255, 159, 125, 0.15) 100%)',
                      border: '2px solid var(--st-primary)',
                    }}
                  >
                    #{job.queue_position}
                  </span>
                </div>

                <p className="text-sm mb-6" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Your story will start generating soon
                </p>

                {/* Premium status badge */}
                {isPremium ? (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'linear-gradient(135deg, rgba(255, 179, 71, 0.15) 0%, rgba(255, 140, 97, 0.1) 100%)', border: '1px solid #FFB347' }}>
                    <Crown className="w-4 h-4" style={{ color: '#FFB347' }} />
                    <span className="text-sm font-semibold" style={{ fontFamily: 'Quicksand, sans-serif', color: '#FFB347' }}>
                      Priority Access
                    </span>
                  </div>
                ) : (
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--st-bg-muted)', border: '1px solid var(--st-border-light)' }}>
                    <Crown className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                    <span className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                      Upgrade for priority
                    </span>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => navigate('/library')}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                      color: 'white',
                      fontFamily: 'Quicksand, sans-serif'
                    }}
                  >
                    <BookOpen className="w-4 h-4" />
                    Browse Library
                  </button>
                  <button
                    onClick={() => setShowCancelDialog(true)}
                    disabled={cancelling}
                    className="px-5 py-3 rounded-xl font-semibold text-sm hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-all"
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#EF4444',
                      border: '2px solid #FECACA'
                    }}
                  >
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>

            {/* Cancel Story Confirmation Dialog */}
            <ConfirmDialog
              open={showCancelDialog}
              onOpenChange={setShowCancelDialog}
              title="Cancel Story?"
              description="This story is currently in the queue. Cancelling will stop the process and no credits will be charged."
              confirmText="Cancel Story"
              cancelText="Keep Waiting"
              onConfirm={handleCancelJob}
              loading={cancelling}
              variant="warning"
            />
          </div>
        </div>
      </PageLayout>
    );
  }

  // Processing state
  const currentStepIndex = getCurrentStepIndex();
  const thumbnailUrl = job?.thumbnail ? getImageUrl(job.thumbnail) : null;
  const storyVersion = job?.story_version || 1;

  return (
    <PageLayout>
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-6 md:py-10">

          {/* Main Card */}
          <div className="rounded-2xl shadow-sm overflow-hidden" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>

            {/* Header with thumbnail preview */}
            <div className="relative">
              {/* Background - show thumbnail if available, otherwise decorative pattern */}
              <div
                className="h-28 md:h-36 relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, var(--st-bg-page) 0%, var(--st-border-light) 50%, var(--st-border-medium) 100%)'
                }}
              >
                {thumbnailUrl ? (
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `url(${thumbnailUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      opacity: 0.4
                    }}
                  />
                ) : (
                  /* Decorative floating elements */
                  <>
                    <div className="absolute top-4 left-8 w-3 h-3 rounded-full opacity-40" style={{ background: 'var(--st-primary-hover)' }} />
                    <div className="absolute top-8 right-12 w-2 h-2 rounded-full opacity-30" style={{ background: 'var(--st-primary)' }} />
                    <div className="absolute bottom-6 left-16 w-2 h-2 rounded-full opacity-25" style={{ background: '#FFB347' }} />
                    <div className="absolute top-6 right-1/4 w-4 h-4 rounded-full opacity-20" style={{ background: 'var(--st-primary-hover)' }} />
                  </>
                )}
              </div>

              {/* Centered icon badge */}
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-8">
                <div className="relative">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
                    style={{
                      background: 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)',
                      boxShadow: '0 8px 24px rgba(255, 140, 97, 0.35)'
                    }}
                  >
                    <Sparkles className="w-7 h-7 text-white animate-pulse" />
                  </div>
                  {/* Version badge */}
                  <div
                    className="absolute -top-0.5 -right-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold"
                    style={{
                      background: storyVersion === 3 ? '#8B5CF6' : storyVersion === 2 ? '#3B82F6' : '#6B7280',
                      color: 'white',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    v{storyVersion}
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="pt-12 pb-6 px-5 md:px-8">
              {/* Title */}
              <div className="text-center mb-5">
                <h1 className="text-xl md:text-2xl font-bold mb-1" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
                  Creating Your Story
                </h1>
                <p className="text-sm" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  {job?.message || 'Working on your masterpiece...'}
                </p>
              </div>

              {/* Enhanced Progress Section with "Ready to Read" indicator */}
              {(() => {
                const totalPages = job?.total_pages || 12;
                const pagesWithImages = job?.pages_with_images || 0;
                const readyToReadThreshold = 3;

                // Calculate where "Ready to Read" appears in OVERALL progress
                // Images start generating at ~45% overall progress (after structure, thumbnail, narrative, splitting, layout, prompts)
                // Each image is roughly 3% of overall progress (images are ~35% of total, so 35/12 ≈ 3% per image)
                // So 3 images ready = 45% + (3 * 3%) = ~54%
                const imageStartPercent = 45; // When "Painting illustrations" step starts
                const percentPerImage = 35 / totalPages; // Images are roughly 35% of total progress
                const readyToReadPercent = imageStartPercent + (readyToReadThreshold * percentPerImage);

                const isReadyToRead = pagesWithImages >= readyToReadThreshold && job?.story_id;
                const overallProgress = job?.progress || 0;

                return (
                  <div className="mb-5">
                    {/* Start Reading Now - Show prominently when ready */}
                    {isReadyToRead && (
                      <div className="mb-4 p-4 rounded-xl relative overflow-hidden" style={{
                        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(16, 185, 129, 0.1) 100%)',
                        border: '2px solid rgba(34, 197, 94, 0.4)'
                      }}>
                        {/* Animated background pulse */}
                        <div className="absolute inset-0 opacity-20" style={{
                          background: 'radial-gradient(circle at center, rgba(34, 197, 94, 0.3) 0%, transparent 70%)',
                          animation: 'pulse 2s ease-in-out infinite'
                        }} />

                        <div className="relative text-center">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                            <p className="text-sm font-bold uppercase tracking-wide" style={{
                              fontFamily: 'Quicksand, sans-serif',
                              color: '#16A34A'
                            }}>
                              Ready to Read!
                            </p>
                          </div>
                          <p className="text-xs mb-3" style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: '#22C55E'
                          }}>
                            {pagesWithImages} of {totalPages} pages ready • Start reading while the rest generates
                          </p>
                          <button
                            onClick={() => navigate(`/story/${job.story_id}?streaming=true`)}
                            className="flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-xl font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
                            style={{
                              background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
                              color: 'white',
                              fontFamily: 'Chewy, cursive',
                              fontSize: '1.15rem',
                              boxShadow: '0 8px 24px rgba(34, 197, 94, 0.4)'
                            }}
                          >
                            <Play className="w-6 h-6" />
                            Start Reading Now!
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Progress Bar with "Ready to Read" marker */}
                    <div className="p-4 rounded-xl bg-orange-50/50 dark:bg-white/5" style={{ border: '1px solid var(--st-border-light)' }}>
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-semibold uppercase tracking-wide" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          Overall Progress
                        </span>
                        <span
                          className="text-sm font-bold"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: 'var(--st-primary)'
                          }}
                        >
                          {overallProgress}%
                        </span>
                      </div>

                      {/* Main progress bar with marker */}
                      <div className="relative">
                        <div className="h-3 rounded-full overflow-hidden" style={{ background: 'var(--st-border-light)' }}>
                          <div
                            className="h-full rounded-full transition-all duration-700 ease-out relative"
                            style={{
                              width: `${Math.max(overallProgress, 2)}%`,
                              background: 'linear-gradient(90deg, #FFB347 0%, var(--st-primary-hover) 50%, var(--st-primary) 100%)'
                            }}
                          >
                            {/* Shimmer effect */}
                            <div
                              className="absolute inset-0 opacity-30"
                              style={{
                                background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.5) 50%, transparent 100%)',
                                animation: 'shimmer 2s infinite'
                              }}
                            />
                          </div>
                        </div>

                        {/* "Ready to Read" marker line */}
                        <div
                          className="absolute top-0 bottom-0 w-0.5 transition-all duration-300"
                          style={{
                            left: `${readyToReadPercent}%`,
                            background: isReadyToRead ? '#16A34A' : '#9CA3AF'
                          }}
                        >
                          {/* Marker dot */}
                          <div
                            className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full border-2 border-white"
                            style={{ background: isReadyToRead ? '#16A34A' : '#9CA3AF' }}
                          />
                        </div>
                      </div>

                      {/* Legend below progress bar */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2 h-2 rounded-full" style={{ background: isReadyToRead ? '#16A34A' : '#9CA3AF' }} />
                          <span className="text-[10px] font-medium" style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: isReadyToRead ? '#16A34A' : 'var(--st-text-muted)'
                          }}>
                            {isReadyToRead ? '✓ Ready to read' : `Ready at ${readyToReadThreshold} pages`}
                          </span>
                        </div>
                        <span className="text-[10px]" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                          {pagesWithImages}/{totalPages} illustrations
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Steps Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-5">
                {STEPS.filter(step => {
                  // Only show videos step if animations are enabled
                  if (step.key === 'videos') {
                    return job?.enable_animations === true;
                  }
                  return true;
                }).map((step) => {
                  const StepIcon = step.Icon;
                  // Use original STEPS index for status comparison
                  const stepIndex = STEPS.findIndex(s => s.key === step.key);
                  const isComplete = stepIndex < currentStepIndex;
                  const isCurrent = stepIndex === currentStepIndex;
                  const isPending = stepIndex > currentStepIndex;

                  return (
                    <div
                      key={step.key}
                      className={`flex items-center gap-3 p-3 rounded-xl transition-all border ${
                        isCurrent
                          ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800/50'
                          : isComplete
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/50'
                            : 'bg-orange-50/30 dark:bg-white/5 border-[var(--st-border-light)]'
                      }`}
                    >
                      {/* Icon */}
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{
                          background: isCurrent
                            ? 'linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)'
                            : isComplete
                              ? 'linear-gradient(135deg, #4ADE80 0%, #22C55E 100%)'
                              : 'var(--st-border-light)'
                        }}
                      >
                        {isComplete ? (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        ) : isCurrent ? (
                          <Loader2 className="w-4 h-4 text-white animate-spin" />
                        ) : (
                          <StepIcon className="w-4 h-4" style={{ color: 'var(--st-text-muted)' }} />
                        )}
                      </div>

                      {/* Label & Status */}
                      <div className="flex-1 min-w-0">
                        <p
                          className="text-sm font-semibold truncate"
                          style={{
                            fontFamily: 'Quicksand, sans-serif',
                            color: isPending ? 'var(--st-text-muted)' : 'var(--st-text-heading)'
                          }}
                        >
                          {step.label}
                        </p>
                        <p className="text-xs font-medium" style={{ fontFamily: 'Quicksand, sans-serif' }}>
                          {isComplete && <span className="text-green-600 dark:text-green-400">Complete</span>}
                          {isCurrent && (
                            <span style={{ color: 'var(--st-primary)' }}>
                              {step.key === 'images' && job
                                ? `${job.pages_with_images || 0}/${job.total_pages} pages`
                                : step.key === 'videos' && job
                                  ? `${job.pages_with_videos || 0}/${job.total_videos || '?'} scenes`
                                  : step.key === 'audio' && job
                                    ? `${job.pages_with_audio || 0}/${job.total_pages} pages`
                                    : 'In progress...'}
                            </span>
                          )}
                          {isPending && <span style={{ color: 'var(--st-text-muted)' }}>Waiting</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer Actions */}
              <div className="pt-4 border-t" style={{ borderColor: 'var(--st-border-light)' }}>
                <p className="text-xs text-center mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>
                  Your story will be saved automatically. Feel free to browse while you wait!
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => navigate('/library')}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)',
                      color: 'var(--st-text-heading)',
                      fontFamily: 'Quicksand, sans-serif',
                      border: '1px solid var(--st-border-medium)'
                    }}
                  >
                    <BookOpen className="w-4 h-4" />
                    Browse Library
                  </button>
                  <button
                    onClick={() => setShowCancelDialog(true)}
                    disabled={cancelling}
                    className="px-4 py-2.5 rounded-xl font-semibold text-sm hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-all"
                    style={{
                      fontFamily: 'Quicksand, sans-serif',
                      color: '#EF4444',
                      border: '1px solid #FECACA'
                    }}
                  >
                    {cancelling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cancel'}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Cancel Story Confirmation Dialog */}
          <ConfirmDialog
            open={showCancelDialog}
            onOpenChange={setShowCancelDialog}
            title={job?.progress >= 50 ? "⚠️ Story Almost Complete!" : "Cancel Story?"}
            description={
              job?.progress >= 75
                ? `Your story is ${job?.progress || 0}% complete with ${job?.pages_with_images || 0} illustrations already created. Cancelling now will DELETE all this progress permanently. Are you sure?`
                : job?.progress >= 50
                  ? `Your story is ${job?.progress || 0}% complete. Cancelling will delete all progress including any pages already generated. No credits will be charged.`
                  : `Your story is ${job?.progress || 0}% complete. Cancelling will stop the process and no credits will be charged.`
            }
            confirmText={job?.progress >= 50 ? "Delete Anyway" : "Cancel Story"}
            cancelText={job?.progress >= 50 ? "Keep Creating" : "Go Back"}
            onConfirm={handleCancelJob}
            loading={cancelling}
            variant={job?.progress >= 50 ? "destructive" : "warning"}
          />
        </div>
      </div>
    </PageLayout>
  );
};

export default GeneratingPage;
